import { IconPlus, IconRefresh } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { SortingState } from "@tanstack/react-table";
import { Button } from "antd";
import { useEffect } from "react";
import { useEnvironmentsPaged } from "@/common/artemis-operations/environments";
import { useOrganizationMetrics } from "@/common/artemis-operations/organization";
import {
	cardSortToKey,
	keyToSort,
} from "@/common/artemis-operations/use-paged-list";
import type {
	EnvironmentClassification,
	EnvironmentProvider,
	EnvironmentStatus,
} from "@/common/model";
import {
	BasePageContainer,
	CustomEmpty,
	CustomIcon,
	QueryGuard,
} from "@/components/base";
import { CardBody, CardViewer } from "@/components/card-viewer";
import {
	EnvironmentClassificationLabelMap,
	EnvironmentClassificationMap,
	EnvironmentProviderLabelMap,
	EnvironmentStatusBadge,
	EnvironmentStatusLabelMap,
} from "@/components/environments/environment-badges";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";
import { useWizards } from "@/contexts/wizard-context";

export const Route = createFileRoute(
	"/_authenticated/$orgSlug/workspaces/$workspaceId/environments/",
)({ component: Environments });

const asTime = (v: unknown) => new Date(v as string).getTime();

const labelOptions = (map: Record<string, string>) =>
	Object.entries(map).map(([value, label]) => ({ value, label }));

function Environments() {
	const navigate = useNavigate();
	const { setTitle } = usePageTitle();
	const { setActions } = useActions();
	const { openEnvironmentCreateWizard } = useWizards();
	const { orgSlug, workspaceId } = Route.useParams();
	const list = useEnvironmentsPaged(workspaceId);
	const { data: metrics } = useOrganizationMetrics();
	const quotaReached =
		metrics?.maxEnvironments != null &&
		metrics.environmentCount >= metrics.maxEnvironments;

	useEffect(() => {
		setTitle("Environments");
		setActions([
			{
				type: "button",
				id: "refresh",
				icon: <CustomIcon icon={IconRefresh} />,
				onClick: () => list.refetch(),
			},
			{
				type: "button",
				id: "create-environment",
				tooltip: "New Environment",
				disabled: quotaReached,
				icon: <CustomIcon icon={IconPlus} />,
				onClick: () => openEnvironmentCreateWizard(),
			},
		]);
	}, [
		setTitle,
		setActions,
		openEnvironmentCreateWizard,
		list.refetch,
		quotaReached,
	]);

	const noFilters =
		!list.search &&
		!list.filters.status &&
		!list.filters.provider &&
		!list.filters.classification;

	if (!list.loading && !list.isError && list.totalCount === 0 && noFilters) {
		return (
			<BasePageContainer>
				<CustomEmpty description="environments" expanded>
					<Button
						type="primary"
						title="Environment"
						disabled={quotaReached}
						onClick={() => openEnvironmentCreateWizard()}
					>
						Create Environment
					</Button>
				</CustomEmpty>
			</BasePageContainer>
		);
	}

	return (
		<QueryGuard isPending={list.loading} isError={list.isError}>
			<BasePageContainer>
				<CardViewer
					items={list.rows}
					getKey={(e) => e.id}
					server={{
						search: list.search,
						onSearchChange: list.setSearch,
						filterValues: {
							status: list.filters.status,
							provider: list.filters.provider,
							classification: list.filters.classification,
						},
						onFilterChange: (k, v) => list.setFilter(k, v),
						sortValue: cardSortToKey(list.sort),
						onSortChange: (v) =>
							list.setSort(keyToSort[v] ?? ([] as SortingState)),
						totalCount: list.totalCount,
						hasNextPage: list.hasNextPage,
						hasPreviousPage: list.hasPreviousPage,
						onNextPage: list.onNextPage,
						onPreviousPage: list.onPreviousPage,
						pageSize: 50,
						pageIndex: list.pageIndex,
					}}
					onOpen={(e) =>
						navigate({
							to: "/$orgSlug/workspaces/$workspaceId/environments/$environmentId",
							params: { orgSlug, workspaceId, environmentId: e.id },
						})
					}
					searchText={(e) => e.name}
					searchPlaceholder="Search environments"
					filters={[
						{
							key: "status",
							allLabel: "All statuses",
							options: labelOptions(EnvironmentStatusLabelMap),
							predicate: (e, v) => e.status === (v as EnvironmentStatus),
						},
						{
							key: "provider",
							allLabel: "All providers",
							options: labelOptions(EnvironmentProviderLabelMap),
							predicate: (e, v) => e.provider === (v as EnvironmentProvider),
						},
						{
							key: "classification",
							allLabel: "All classifications",
							width: 170,
							options: labelOptions(EnvironmentClassificationLabelMap),
							predicate: (e, v) =>
								e.classification === (v as EnvironmentClassification),
						},
					]}
					sorts={[
						{
							value: "newest",
							label: "Newest",
							compare: (a, b) => asTime(b.createdAt) - asTime(a.createdAt),
						},
						{
							value: "oldest",
							label: "Oldest",
							compare: (a, b) => asTime(a.createdAt) - asTime(b.createdAt),
						},
						{
							value: "name",
							label: "Name A–Z",
							compare: (a, b) => a.name.localeCompare(b.name),
						},
					]}
					renderCard={(e) => (
						<CardBody
							title={e.name}
							description={
								EnvironmentProviderLabelMap[e.provider] +
								", " +
								EnvironmentClassificationMap[e.classification].label
							}
							extra={<EnvironmentStatusBadge status={e.status} />}
						/>
					)}
				/>
			</BasePageContainer>
		</QueryGuard>
	);
}
